import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  useUpdateClientServiceAttendanceMutation,
  useUpdateClientServiceStatusMutation,
  useLazyGetAssessmentFormQuery,
  useLazyGetAssessmentValuesQuery,
} from "../../services/ClientService";
import { useGetAttendanceStatusReasonsQuery } from "../../services/UtilityService";
import { getIncompleteRequired } from "../psychosocialAssessment/requiredCompletion";

/**
 * Shared "perform" actions for the SessionInfoDialog — set attendance and mark a service
 * performed (with a bypassable "required form not complete" warning). Used by every entry point
 * into the dialog (calendar, nav-bar alert bell, client-grid recent services) so all three offer
 * the same actions.
 *
 * @param {(id:number, patch:object) => void} patchClient  Optimistically patch a client's fields
 *        in the consumer's own dialog state (each surface owns different state).
 * @param {() => void} [onChanged]  Called after a successful change so the consumer can refetch.
 *
 * @returns {{ attendanceOptions, onSetAttendance, onMarkPerformed, markPerformedBusyId,
 *            performWarningDialog }} props to spread onto SessionInfoDialog + a dialog to render.
 */
export function useSessionServiceActions({ patchClient, onChanged } = {}) {
  const { data: attendanceOptions = [] } = useGetAttendanceStatusReasonsQuery();
  const [updateClientServiceAttendance] = useUpdateClientServiceAttendanceMutation();
  const [updateClientServiceStatus] = useUpdateClientServiceStatusMutation();
  const [fetchAssessmentForm] = useLazyGetAssessmentFormQuery();
  const [fetchAssessmentValues] = useLazyGetAssessmentValuesQuery();

  const [pendingPerform, setPendingPerform] = useState(null);
  const [checkingPerformId, setCheckingPerformId] = useState(null);

  const patch = (id, p) => { patchClient?.(id, p); };
  const changed = () => { onChanged?.(); };

  const onSetAttendance = async (clientEvent, attendanceStatusId) => {
    const id = clientEvent?.id;
    if (!id) return;
    await updateClientServiceAttendance({ clientServiceId: id, attendanceStatusId });
    patch(id, {
      attendanceStatusId,
      attendanceStatus: attendanceOptions.find((o) => Number(o.id) === Number(attendanceStatusId))?.name ?? null,
    });
    changed();
  };

  // Actually set the service to Performed (form complete, or the user bypassed the warning).
  const performMarkPerformed = async (clientEvent) => {
    const id = clientEvent?.id;
    if (!id) return;
    await updateClientServiceStatus({ clientServiceId: id, statusId: 2 }); // 2 = Performed
    patch(id, { statusId: 2 });
    changed();
  };

  // Check the service's assessment form for unanswered required items first; incomplete → warn
  // (listing what's missing) but let the user proceed anyway.
  const onMarkPerformed = async (clientEvent) => {
    const id = clientEvent?.id;
    if (!id) return;
    const formId = clientEvent?.psychosocialAssessmentFormId;
    const clientIdForForm = clientEvent?.clientId;

    if (formId && clientIdForForm) {
      try {
        setCheckingPerformId(id);
        const [formRes, valuesRes] = await Promise.all([
          fetchAssessmentForm({ formId, versionId: clientEvent?.psychosocialAssessmentFormVersion ?? 0 }).unwrap(),
          fetchAssessmentValues({ clientId: clientIdForForm, formId, clientServiceId: id }).unwrap(),
        ]);
        const missing = getIncompleteRequired(formRes, valuesRes);
        if (missing.length > 0) {
          setPendingPerform({ clientEvent, missing });
          return; // wait for the user's choice
        }
      } catch (e) {
        // Couldn't evaluate the form — don't block the status change, just proceed.
        console.error("Required-field check failed; marking performed anyway.", e);
      } finally {
        setCheckingPerformId(null);
      }
    }

    await performMarkPerformed(clientEvent);
  };

  const performWarningDialog = (
    <Dialog open={!!pendingPerform} onClose={() => setPendingPerform(null)} maxWidth="xs" fullWidth sx={{ zIndex: 1600 }}>
      <DialogTitle sx={{ fontSize: 16, display: "flex", alignItems: "center", gap: 1 }}>
        <WarningAmberIcon fontSize="small" sx={{ color: "#ed6c02" }} />
        Form not complete
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontSize: 13, mb: 1 }}>
          This service still has{" "}
          <strong>
            {pendingPerform?.missing?.length} unanswered required item
            {pendingPerform?.missing?.length === 1 ? "" : "s"}
          </strong>. You can still mark it performed.
        </DialogContentText>
        <Box sx={{ maxHeight: 200, overflowY: "auto", border: "1px solid #eee", borderRadius: 1, px: 1.5, py: 1, bgcolor: "#fffdf7" }}>
          {(pendingPerform?.missing ?? []).map((m, i) => (
            <Typography key={`${m.label}-${i}`} sx={{ fontSize: 12, lineHeight: 1.6 }}>
              • {m.label}
              {m.kind === "group" && (
                <Typography component="span" sx={{ fontSize: 11, color: "text.secondary" }}> (group)</Typography>
              )}
              {m.section && (
                <Typography component="span" sx={{ fontSize: 11, color: "text.secondary" }}> — {m.section}</Typography>
              )}
            </Typography>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={() => setPendingPerform(null)} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button
          variant="contained"
          color="warning"
          sx={{ textTransform: "none" }}
          onClick={async () => {
            const target = pendingPerform?.clientEvent;
            setPendingPerform(null);
            if (target) await performMarkPerformed(target);
          }}
        >
          Mark performed anyway
        </Button>
      </DialogActions>
    </Dialog>
  );

  return {
    attendanceOptions,
    onSetAttendance,
    onMarkPerformed,
    markPerformedBusyId: checkingPerformId,
    performWarningDialog,
  };
}
